import { experiences } from '../Constant';

/**
 * Function to calculate the experience duration between two dates.
 * If the end date is null, it uses the current date.
 * It returns a string representing the duration in years and months.
 * @param startDate : string
 * @param endDate : string | null
 * @returns {string} - A string representing the experience duration in years and months.
 *
 */
export const calculateExperience = (
  startDate: string,
  endDate: string | null
) => {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();

  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const years = Math.floor(diffDays / 365);
  const months = Math.floor((diffDays % 365) / 30);

  if (years > 0 && months > 0) {
    return `${years} year${years > 1 ? 's' : ''} ${months} month${
      months > 1 ? 's' : ''
    }`;
  } else if (years > 0) {
    return `${years} year${years > 1 ? 's' : ''}`;
  } else if (months > 0) {
    return `${months} month${months > 1 ? 's' : ''}`;
  } else {
    return 'Less than a month';
  }
};

/**
 * Function to format a date string to a more readable format of "MMM YYYY".
 * It uses the `toLocaleDateString` method to format the date.
 * The date string should be in a format that can be parsed by the Date constructor.
 * @param dateStr : string - The date string to be formatted.
 * @returns {string} - A string representing the date in "MMM YYYY" format.
 */
export const formatDateToMonthYear = (dateStr: string | null): string => {
  if (dateStr) {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
    });
  }
  return 'Present'; // Return 'Present' if dateStr is null
};

/**
 *  Function to calculate the period between two dates.
 *  It formats the start date and end date to "MMM YYYY" format.
 *  If the end date is null, it uses the current date.
 *  It returns a string representing the period in "MMM YYYY - MMM YYYY" format.
 * @param startDate : string
 * @param endDate : string | null
 * @returns : {string} - A string representing the period in "MMM YYYY - MMM YYYY" format.
 */
export const calculatePeriod = (
  startDate: string,
  endDate: string | null
): string => {
  return `${formatDateToMonthYear(startDate)} - ${formatDateToMonthYear(
    endDate
  )}`;
};

/**
 * Function to calculate the total experience from the experiences array.
 * It iterates through each experience, calculates the difference in days between the start and end dates,
 * and accumulates the total days. It then converts the total days into months and years.
 * Finally, it returns a string representing the total experience in years, rounded to one decimal place
 * @returns {string} - A string representing the total experience in years, e.g., "4.8+ years".
 */
export const calculateTotalExperience = () => {
  let totalDays = 0;

  experiences.forEach((exp) => {
    const start = new Date(exp.startDate);
    const end = exp.endDate ? new Date(exp.endDate) : new Date();
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    totalDays += diffDays;
  });

  const totalMonths = totalDays / 30; // approximate
  const totalYears = totalMonths / 12;

  const roundedYears = (Math.floor(totalYears * 10) / 10).toFixed(1); // e.g., 4.83 => 4.8

  return `${roundedYears}+ years`;
};
